import React from 'react';

import {
  faFileArrowUp, faFolderMinus, faFolderPlus, faMapLocationDot, faPenToSquare,
} from '@fortawesome/pro-regular-svg-icons';
import { faLock } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@buildoutinc/blueprint-react/ui/Badge';
import { Table } from '@buildoutinc/blueprint-react/ui/Table';

import { Checkbox } from 'components/ui/checkbox';
import Dropdown from 'components/dropdown';
import { formatDate } from 'helpers/formatters/date_time_formats';

import { deleteSkipReason } from './bulk_delete_modal';
import OrientationBadge from './orientation_badge';
import UsedInPopover from './used_in_popover';
import { PAGE_TYPE_PARAMS, TemplateType, type PageRecord } from './types';

interface Props {
  page: PageRecord;
  detailBasePath: string;
  detailCompanyId: number | null;
  selected: boolean;
  onSelectedChange: (page: PageRecord, selected: boolean) => void;
  onReplaceFile: (page: PageRecord) => void;
  onAddToDocuments: (page: PageRecord) => void;
  onRemoveFromDocuments: (page: PageRecord) => void;
  onDelete: (page: PageRecord) => void;
  // Set only when the company has the Market Overviews collection.
  onMoveToMarketOverviews?: (page: PageRecord) => void;
}

export default function PageRow({
  page, detailBasePath, detailCompanyId, selected, onSelectedChange, onReplaceFile, onAddToDocuments,
  onRemoveFromDocuments, onDelete, onMoveToMarketOverviews,
}: Props) {
  const isPdf = page.templateType === TemplateType.Pdf;
  const replaceDisabledMessage = isPdf
    ? `Only ${page.createdByName || 'its owner'} can replace this file`
    : 'Only PDF pages have a file to replace';
  const deleteBlocker = deleteSkipReason(page);
  const detailUrl = `${detailBasePath}/${PAGE_TYPE_PARAMS[page.templateType]}/${page.id}` +
    `${detailCompanyId ? `?company_id=${detailCompanyId}` : ''}`;

  return (
    <Table.Row>
      <Table.Cell style={{ width: '32px' }}>
        <Checkbox
          aria-label={`Select ${page.name}`}
          checked={selected}
          onCheckedChange={(checked: boolean | 'indeterminate') => onSelectedChange(page, checked === true)}
        />
      </Table.Cell>
      <Table.Cell>
        <a href={detailUrl}>{page.name}</a>
        {page.templateType !== TemplateType.Tree && page.userLabel && (
          <Badge className="ms-2" variant="outline">{page.userLabel}</Badge>
        )}
        {page.inPageCollection && (
          <Badge className="ms-2" variant="secondary">
            Market Overviews · {page.marketName ?? 'No market'}
          </Badge>
        )}
      </Table.Cell>
      <Table.Cell>
        <div className="d-flex flex-wrap gap-1">
          {(page.lockedTags || []).map(tag => (
            <Badge key={tag} title="Set by Buildout. Can't be removed." variant="outline">
              <FontAwesomeIcon className="me-1" icon={faLock} />
              {tag}
            </Badge>
          ))}
          {(page.tags || []).map(tag => (
            <Badge key={tag} variant="outline">{tag}</Badge>
          ))}
        </div>
      </Table.Cell>
      <Table.Cell>
        <OrientationBadge orientation={page.orientation} />
      </Table.Cell>
      <Table.Cell>
        {page.documentsCount === 0 ? (
          <span className="text-muted">Not used</span>
        ) : (
          <UsedInPopover detailUrl={detailUrl} documents={page.documents} documentsCount={page.documentsCount} />
        )}
      </Table.Cell>
      <Table.Cell>{page.createdByName || 'Buildout Defaults'}</Table.Cell>
      <Table.Cell>{page.updatedAt ? formatDate(page.updatedAt) : '---'}</Table.Cell>
      <Dropdown.Cell style={{ height: 'auto' }}>
        <Dropdown.Item
          icon={faPenToSquare}
          label="Edit page"
          onClick={() => {
            window.location.href = detailUrl;
          }}
        />
        <Dropdown.Item
          disabled={!isPdf || page.ownedByContext === false}
          disabledMessage={replaceDisabledMessage}
          icon={faFileArrowUp}
          label="Replace file"
          onClick={() => onReplaceFile(page)}
        />
        <Dropdown.Item
          disabled={page.inPageCollection}
          disabledMessage="The Market Overview slot adds this page to documents"
          icon={faFolderPlus}
          label="Add to documents"
          onClick={() => onAddToDocuments(page)}
        />
        <Dropdown.Item
          disabled={page.documentsCount === 0}
          disabledMessage="This page is not used in any documents"
          icon={faFolderMinus}
          label="Remove from documents"
          onClick={() => onRemoveFromDocuments(page)}
        />
        {/* Only a page the company owns can join its collection */}
        {onMoveToMarketOverviews && isPdf && page.ownedByContext && !page.inPageCollection && (
          <Dropdown.Item
            icon={faMapLocationDot}
            label="Move to Market Overviews"
            onClick={() => onMoveToMarketOverviews(page)}
          />
        )}
        <Dropdown.Item
          disabled={deleteBlocker !== null}
          disabledMessage={deleteBlocker}
          isDelete
          label="Delete page"
          onClick={() => onDelete(page)}
        />
      </Dropdown.Cell>
    </Table.Row>
  );
}
